import { FACT_HIDE_ORDER, FACT_TEXT_ORDER, type TableCardFactKind } from "./tableCardFacts";

/**
 * Къде и в какъв вид стоят фактите за сметката в кутийката на масата. Чиста
 * функция върху мерките от DOM (`measureCardFacts`), без DOM. Котвите не
 * мърдат: името е горе вляво, сервитьорът горе вдясно, сумата долу вдясно.
 * Фактите растат от сумата нагоре: всеки на свой ред, докато има височина;
 * които не стигат — на един ред до сумата (или точно над нея), всички в един
 * и същи вид: с текста, със стойностите или само иконки. До името — нищо.
 * Името не губи ред, сумата не се реже.
 */

/** Видът на факта: целият текст („4 гости", „-3.00 € на цялата сметка"),
 *  само стойността („4", „-3.00 €", депозитът — сумата; резервацията — името)
 *  или само иконката. */
export type FactForm = "text" | "value" | "icon";

const FORMS_RICHEST_FIRST: readonly FactForm[] = ["text", "value", "icon"];

/** До сумата (`gap-2`). */
const SUM_GAP = 8;
/** Общият етикет („2 гости", „на цялата сметка") иска поне двойно до
 *  сумата — иначе „2 гости 6.00" се чете като една фраза. */
const LABEL_EXTRA_GAP = 8;
/** Между редовете (`gap-y-1`). */
const ROW_GAP = 4;
/** Най-малко видим текст на име (~5 букви + „…" при 12px) — без огризки
 *  като „Пет…". */
const MIN_TEXT = 56;
/** Допуск при сравняването на мерки (дробни пиксели). */
export const EPS = 0.5;

/** Елемент на реда с факти: индекс във фактите (ред на четене) или „+N". */
export type LineItem = number | "more";

/** Факт на свой ред — и видът му по ширината на кутийката. */
export interface FactRowPlan {
  index: number;
  form: FactForm;
}

export interface CardFactsPlan {
  /** Фактите на свой ред над сумата, в реда на четене. */
  rows: FactRowPlan[];
  /** Фактите на един ред — до сумата или точно над нея (`lineAboveSum`);
   *  в реда на четене, „+N" е първо. */
  line: LineItem[];
  /** Видът на всички факти на реда — един и същ. */
  lineForm: FactForm;
  /** Редът е над сумата — до нея не стига място; сумата е сама на реда си. */
  lineAboveSum: boolean;
  /** Скритите в „+N", в реда на четене. */
  hidden: number[];
}

/** Без факти — кутийката е байт за байт като без информация. */
export const EMPTY_PLAN: CardFactsPlan = {
  rows: [],
  line: [],
  lineForm: "icon",
  lineAboveSum: false,
  hidden: [],
};

const indicesOf = (count: number) => Array.from({ length: count }, (_, index) => index);

/** Преди първото мерене (и в jsdom, където няма размери): всички факти със
 *  стойностите до сумата. */
export const defaultPlan = (factCount: number): CardFactsPlan => ({
  ...EMPTY_PLAN,
  line: indicesOf(factCount),
  lineForm: "value",
});

/** Мерките на един факт — от невидимия слой за мерене. */
export interface MeasuredFact {
  kind: TableCardFactKind;
  /** Най-тесният допустим вид (`minWidthOf`) — по видове. Без текст
   *  (клиент без име) видът „text" е стойността. */
  minWidth: Record<FactForm, number>;
  /** Целият текст, без рязане — до сумата при достатъчно височина отива
   *  само цял. */
  textWidth: number;
  /** Текстът е име; общият етикет иска място до сумата (`LABEL_EXTRA_GAP`). */
  textIsName: boolean;
}

/** Най-тесният допустим вид: името в него се реже, но остават поне
 *  `MIN_TEXT` от него (цяло, ако е по-късо); `nameWidth` null — няма какво да
 *  се реже (общият етикет не се реже никога). */
export const minWidthOf = (width: number, nameWidth: number | null) =>
  nameWidth === null ? width : Math.min(width, width - nameWidth + MIN_TEXT);

interface CardFactsInputBase {
  /** В реда на четене — индексите в плана са в този масив. */
  facts: readonly MeasuredFact[];
  /** Ширината на „+N". */
  moreWidth: number;
  /** Между два факта на реда: отстоянията и чертата между тях. */
  dividerWidth: number;
  /** Вътрешната ширина на кутийката (без падинга). */
  innerWidth: number;
  /** Ширината на сумата. */
  sumWidth: number;
}

/** Десктоп, кутийка на два реда (височина ≥ 70). */
export interface TwoRowCardInput extends CardFactsInputBase {
  mode: "twoRow";
  /** Височината на горния ред без нищо от информацията (основата). */
  topHeight: number;
  /** Височината на един ред информация. */
  rowHeight: number;
  /** Редовата височина на името на масата. */
  nameLineHeight: number;
  /** На колко реда е цялото име (без рязане). */
  nameLines: number;
  /** На колко реда е видимо в основата (`--name-lines`). */
  nameBaseLines: number;
  /** Името се реже в основата. */
  nameIsCut: boolean;
}

/** Десктоп, кутийка на един ред (височина 44–69). */
export interface OneLineCardInput extends CardFactsInputBase {
  mode: "oneLine";
  /** Името на масата на един ред, без рязане. */
  nameWidth: number;
  /** Отстоянието между частите на реда (`gap-x` на кутийката; под 112px —
   *  по-малко, за да стигне място за „+N"). До сумата пак остават `SUM_GAP`. */
  lineGap: number;
}

/** Телефон и таблет (`TableCard compact`): ширината е фиксирана, височината
 *  следва съдържанието. */
export interface CompactCardInput extends CardFactsInputBase {
  mode: "compact";
}

export type CardFactsInput = TwoRowCardInput | OneLineCardInput | CompactCardInput;

const fits = (width: number, room: number) => width <= room + EPS;

/** Индексите на фактите, подредени по дадена подредба на видовете. */
const rankBy = (facts: readonly MeasuredFact[], order: readonly TableCardFactKind[]) =>
  indicesOf(facts.length).sort((a, b) => order.indexOf(facts[a].kind) - order.indexOf(facts[b].kind));

/** Ширината на реда с факти, всички във вид `form`, с чертите между тях; до
 *  сумата общият етикет накрая иска `LABEL_EXTRA_GAP`. */
const lineWidthOf = (
  input: CardFactsInputBase,
  items: readonly LineItem[],
  form: FactForm,
  besideSum: boolean,
) => {
  const { facts, moreWidth, dividerWidth } = input;
  const last = items[items.length - 1];
  const labelLast = besideSum && form === "text" && last !== "more" && !facts[last].textIsName;
  return items.reduce<number>(
    (sum, item, position) =>
      sum + (position > 0 ? dividerWidth : 0) + (item === "more" ? moreWidth : facts[item].minWidth[form]),
    labelLast ? LABEL_EXTRA_GAP : 0,
  );
};

/** Най-богатият вид, в който целият ред се събира в `room`; null — не се
 *  събира дори само с иконки. */
const lineFormOf = (
  input: CardFactsInputBase,
  items: readonly number[],
  room: number,
  besideSum: boolean,
): FactForm | null =>
  FORMS_RICHEST_FIRST.find((form) => fits(lineWidthOf(input, items, form, besideSum), room)) ?? null;

interface LineWithMore {
  line: LineItem[];
  hidden: number[];
}

/**
 * Иконките с „+N": опитва с 1, 2, … скрити по `FACT_HIDE_ORDER` и взима
 * първия опит, който се събира. „+N" е първо — там, където са скритите
 * (предимно хората).
 */
const iconsWithMore = (
  input: CardFactsInputBase,
  items: readonly number[],
  room: number,
): LineWithMore | null => {
  const hideRank = rankBy(input.facts, FACT_HIDE_ORDER).filter((index) => items.includes(index));
  for (let hiddenCount = 1; hiddenCount <= items.length; hiddenCount += 1) {
    const hidden = new Set(hideRank.slice(0, hiddenCount));
    const line: LineItem[] = ["more", ...items.filter((index) => !hidden.has(index))];
    if (fits(lineWidthOf(input, line, "icon", false), room)) {
      return { line, hidden: [...hidden].sort((a, b) => a - b) };
    }
  }
  return null;
};

/** Видът на факта на свой ред: най-богатият, който се събира в ширината;
 *  иконката — винаги. */
const rowFormOf = (fact: MeasuredFact, innerWidth: number): FactForm =>
  FORMS_RICHEST_FIRST.find((form) => fits(fact.minWidth[form], innerWidth)) ?? "icon";

// Колко свободни реда (k) остават под името, след като то си вземе нужните
// редове — информацията никога не му взима ред. Рязано име вече е взело
// всичките си редове: k = 0.
const freeRowsOf = (input: TwoRowCardInput): number => {
  if (input.nameIsCut || input.nameLineHeight <= 0) return 0;
  const neededLines = Math.max(1, Math.min(input.nameLines, input.nameBaseLines));
  const nameLinesWith = (rows: number) =>
    Math.floor((input.topHeight - rows * (input.rowHeight + ROW_GAP) + EPS) / input.nameLineHeight);
  let rows = 0;
  while (rows < input.facts.length && nameLinesWith(rows + 1) >= neededLines) rows += 1;
  return rows;
};

const planTwoRow = (input: TwoRowCardInput): CardFactsPlan => {
  const { facts, innerWidth, sumWidth } = input;
  const count = facts.length;
  const all = indicesOf(count);
  const freeRows = freeRowsOf(input);
  const besideRoom = innerWidth - sumWidth - SUM_GAP;
  const rowsOf = (indices: readonly number[]) =>
    indices.map((index) => ({ index, form: rowFormOf(facts[index], innerWidth) }));

  // 1. Височината стига: всеки факт на свой ред; последният — до сумата, но
  // само ако се събира там цял (иначе остава на реда си, не слиза).
  if (count <= freeRows) {
    const last = facts[count - 1];
    if (fits(last.textWidth + (last.textIsName ? 0 : LABEL_EXTRA_GAP), besideRoom)) {
      return { ...EMPTY_PLAN, rows: rowsOf(all.slice(0, -1)), line: [count - 1], lineForm: "text" };
    }
    return { ...EMPTY_PLAN, rows: rowsOf(all) };
  }

  // 2. Не стига: първите r по `FACT_TEXT_ORDER` остават на свой ред, другите
  // слизат на един ред до сумата — в най-богатия вид, в който се събират
  // всички; не се ли събират там дори като иконки — на ред над сумата (с един
  // ред по-малко за останалите).
  const textRank = rankBy(facts, FACT_TEXT_ORDER);
  const splitAt = (rowCount: number) => {
    const onRows = new Set(textRank.slice(0, rowCount));
    return {
      rows: rowsOf(all.filter((index) => onRows.has(index))),
      line: all.filter((index) => !onRows.has(index)),
    };
  };
  const maxRows = Math.min(count - 1, freeRows);
  for (let rowCount = maxRows; rowCount >= 0; rowCount -= 1) {
    const { rows, line } = splitAt(rowCount);
    const besideForm = lineFormOf(input, line, besideRoom, true);
    if (besideForm) return { ...EMPTY_PLAN, rows, line, lineForm: besideForm };
    const aboveForm = rowCount < freeRows ? lineFormOf(input, line, innerWidth, false) : null;
    if (aboveForm) return { ...EMPTY_PLAN, rows, line, lineForm: aboveForm, lineAboveSum: true };
  }

  // 3. Не стигат и иконките — с „+N"; най-малко скрити, при равенство — с
  // повече редове.
  let best: CardFactsPlan | null = null;
  for (let rowCount = maxRows; rowCount >= 0; rowCount -= 1) {
    const { rows, line } = splitAt(rowCount);
    const places = rowCount < freeRows ? [false, true] : [false];
    for (const lineAboveSum of places) {
      const placed = iconsWithMore(input, line, lineAboveSum ? innerWidth : besideRoom);
      if (placed && (!best || placed.hidden.length < best.hidden.length)) {
        best = { rows, line: placed.line, lineForm: "icon", lineAboveSum, hidden: placed.hidden };
      }
    }
  }
  // 4. Нищо — няма място дори за „+N" до сумата (най-тясната кутийка).
  return best ?? EMPTY_PLAN;
};

// На един ред: име → факти → сервитьор → сума. Сервитьорът не се брои — той
// отстъпва първи (`NameIfRoom`: цяло име → инициали → нищо). Фактите — в
// най-богатия вид, в който се събират всички; после иконки с „+N", после
// нищо. До името — `lineGap`, до сумата — поне `SUM_GAP` (сумата е `ml-auto`).
const planOneLine = (input: OneLineCardInput): CardFactsPlan => {
  const { lineGap } = input;
  const room = input.innerWidth - input.nameWidth - input.sumWidth - lineGap - Math.max(lineGap, SUM_GAP);
  const all = indicesOf(input.facts.length);
  const form = lineFormOf(input, all, room, true);
  if (form) return { ...EMPTY_PLAN, line: all, lineForm: form };
  const placed = iconsWithMore(input, all, room);
  return placed ? { ...EMPTY_PLAN, line: placed.line, hidden: placed.hidden } : EMPTY_PLAN;
};

// Телефон и таблет: фактите на един ред до сумата; не се ли събират там дори
// като иконки — на ред над нея (само тогава кутийката расте).
const planCompact = (input: CompactCardInput): CardFactsPlan => {
  const all = indicesOf(input.facts.length);
  const besideForm = lineFormOf(input, all, input.innerWidth - input.sumWidth - SUM_GAP, true);
  if (besideForm) return { ...EMPTY_PLAN, line: all, lineForm: besideForm };
  const aboveForm = lineFormOf(input, all, input.innerWidth, false);
  if (aboveForm) return { ...EMPTY_PLAN, line: all, lineForm: aboveForm, lineAboveSum: true };
  const placed = iconsWithMore(input, all, input.innerWidth);
  return placed
    ? { ...EMPTY_PLAN, line: placed.line, lineAboveSum: true, hidden: placed.hidden }
    : EMPTY_PLAN;
};

/** Планът на кутийката: без факти — `EMPTY_PLAN`; без размери (jsdom, преди
 *  първото мерене) — `defaultPlan`; иначе по вида на кутийката. */
export const planCardFacts = (input: CardFactsInput): CardFactsPlan => {
  if (input.facts.length === 0) return EMPTY_PLAN;
  if (input.innerWidth <= 0) return defaultPlan(input.facts.length);
  switch (input.mode) {
    case "twoRow":
      return planTwoRow(input);
    case "oneLine":
      return planOneLine(input);
    case "compact":
      return planCompact(input);
  }
};
